/**
 * A day of typing, as a dozen entries instead of two hundred.
 *
 * WHY GROUPING AT ALL. The document saves two seconds after somebody stops typing, so an afternoon
 * of work is hundreds of rows that all say the same thing: Amara edited it. The trail has to record
 * every one of them -- that is what a trail is -- but a reader looking for "when did the acceptance
 * criteria change" has to get past them first. Google collapses contiguous edits the same way, and
 * expands on demand.
 *
 * NOTHING IS HIDDEN, ONLY FOLDED. Every event is inside a group and every group says how many it
 * holds. A history that quietly dropped the dull entries would make a document look as though it
 * had been touched six times when it was touched two hundred.
 *
 * WHAT NEVER GETS FOLDED INTO ITS NEIGHBOURS:
 *
 *   - A NAMED version. The whole point of a name is that somebody can find it; burying it inside
 *     "47 edits" would undo that.
 *   - Anything that is not an ordinary save -- created, imported, restored, a suggestion resolved.
 *     Those are the events people go looking for by name.
 *   - An edit by a different person. "Who changed this" is the commonest question a history is
 *     asked, and a group spanning two authors cannot answer it.
 */
import { Version } from "./VersionHistory";

/** How long a gap ends a run of edits. Longer than a pause to think, shorter than a coffee. */
export const GROUP_WINDOW_MS = 10 * 60 * 1000;

/**
 * How long one folded row may cover, however continuous the typing.
 *
 * TWO CONDITIONS, NOT ONE, and the first version had only the gap -- which meant that edits thirty
 * seconds apart folded forever. Forty minutes of steady work became a single row labelled with one
 * time, which hides the thing a reader most wants to see: that it took forty minutes.
 */
export const MAX_GROUP_SPAN_MS = 30 * 60 * 1000;

/** The actions that are never folded together — see the note above. */
const ALWAYS_ALONE = new Set(["CREATED", "IMPORTED", "RESTORED", "NAMED",
                              "SUGGESTION_ACCEPTED", "SUGGESTION_REJECTED",
                              "RECONCILED", "RENAMED", "DELETED"]);

export type VersionGroup = {
    /** The newest version in the group — what the row shows, and what opening it opens. */
    head: Version;
    /** Every version in it, newest first. One entry for a group that was never folded. */
    members: Version[];
    /** True when there is more than one, so the row can offer to expand. */
    folded: boolean;
};

function alone(version: Version): boolean {
    return ALWAYS_ALONE.has(version.Action) || Boolean(version.Name);
}

/**
 * Fold a history into groups, newest first.
 *
 * THE LIST ARRIVES NEWEST FIRST and stays that way: a group's head is the LATEST edit in the run,
 * because that is the state the document reached, and opening a folded group should show where the
 * run ended rather than where it started.
 */
export function groupVersions(versions: Version[]): VersionGroup[] {
    const groups: VersionGroup[] = [];

    for (const version of versions) {
        const open = groups[groups.length - 1];
        const at = new Date(version.At).getTime();
        const previous = open && new Date(open.members[open.members.length - 1].At).getTime();
        const head = open && new Date(open.head.At).getTime();

        const sameRun =
            open &&
            !alone(version) &&
            !alone(open.head) &&
            open.head.ByUSID === version.ByUSID &&
            // CONTIGUOUS: no long pause between this edit and the one beside it.
            Math.abs((previous as number) - at) <= GROUP_WINDOW_MS &&
            // AND BOUNDED: one row may not cover more than MAX_GROUP_SPAN_MS however steady the
            // typing. Without this, edits thirty seconds apart fold forever and forty minutes of
            // work becomes a single row labelled with one time.
            Math.abs((head as number) - at) <= MAX_GROUP_SPAN_MS;

        if (sameRun) {
            open.members.push(version);
            open.folded = true;
        } else {
            groups.push({ head: version, members: [version], folded: false });
        }
    }

    return groups;
}

/** How a folded row describes itself: "47 edits · 10:02–11:14". */
export function foldedLabel(group: VersionGroup, when: (at: string) => string): string {
    const oldest = group.members[group.members.length - 1];
    const span = when(oldest.At) === when(group.head.At)
        ? when(group.head.At)
        : `${when(oldest.At)}–${when(group.head.At)}`;
    return `${group.members.length} edits · ${span}`;
}

/**
 * What the document's size did across a run, as a signed number.
 *
 * READ FROM THE ENDS, not summed: each row records the size AFTER it, so the difference between the
 * oldest and newest in the run is the net change, and adding up per-row deltas would need a row
 * before the first one that may not be in this page of history.
 */
export function netChange(group: VersionGroup): number | null {
    const oldest = group.members[group.members.length - 1];
    if (group.head.Characters == null || oldest.Characters == null) return null;
    return group.head.Characters - oldest.Characters;
}
